import { Box, Divider, Typography, styled } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import DatePicker from 'further-ui/components/DatePicker';
import { Column, Row } from 'further-ui/components/Layout';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import { differenceInDays } from 'date-fns';
import { CurrencyCode } from 'further-types/lp/vehicle';

const PrimaryDot = styled(Box)(({ theme }) => ({
  width: theme.spacing(theme.spacingSizes.xs),
  height: theme.spacing(theme.spacingSizes.xs),
  borderRadius: '2px',
  backgroundColor: theme.palette.primary.dark,
}));

const SecondaryDot = styled(PrimaryDot)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.dark,
}));

type Props = {
  signedCommitmentsValue: number;
  prospectiveCommitmentsValue: number;
  targetCloseDate: CalendarDay | null;
  handleChangeTargetCloseDate: (value: CalendarDay | null) => void;
  vehicleCurrency: CurrencyCode;
  isUpdatingOnboardingSettings: boolean;
};

const CommitmentsDetails: React.FC<Props> = ({
  signedCommitmentsValue,
  prospectiveCommitmentsValue,
  targetCloseDate,
  handleChangeTargetCloseDate,
  vehicleCurrency,
  isUpdatingOnboardingSettings,
}) => {
  const daysDifference = targetCloseDate?.isAfter(new Date())
    ? differenceInDays(
        targetCloseDate.startOfDay(),
        new CalendarDay(new Date()).startOfDay(),
      )
    : null;

  const daysToTargetClose = daysDifference
    ? `${daysDifference} ${pluralize('day', daysDifference)}`
    : '-';

  return (
    <>
      <Column spacing="xs" fullWidth>
        <Typography variant="h6" color="grey.600">
          Completed commitments
        </Typography>
        <Row centered spacing="sm">
          <PrimaryDot />
          <Typography variant="h2">
            {numberToCurrencyString(signedCommitmentsValue, {
              currency: vehicleCurrency,
            })}
          </Typography>
        </Row>
      </Column>

      <Column spacing="xs" fullWidth>
        <Typography variant="h6" color="grey.600">
          Prospective commitments
        </Typography>
        <Row centered spacing="sm">
          <SecondaryDot />
          <Typography variant="h2">
            {numberToCurrencyString(prospectiveCommitmentsValue, {
              currency: vehicleCurrency,
            })}
          </Typography>
        </Row>
      </Column>

      <Divider />
      <Column spacing="sm" fullWidth>
        <Typography variant="h6">Target close date</Typography>
        <FormField size="md">
          <DatePicker
            fullWidth
            value={targetCloseDate}
            onChange={handleChangeTargetCloseDate}
            placeholder="Select date"
            disabled={isUpdatingOnboardingSettings}
          />
        </FormField>
      </Column>

      <Column spacing="sm" fullWidth>
        <Typography variant="h6">Days to target close</Typography>
        <Typography variant="body2" color="grey.900">
          {daysToTargetClose}
        </Typography>
      </Column>
    </>
  );
};

export default CommitmentsDetails;
